import React, { useState } from "react";
import { usersApi } from "@services/api/users";
import { useDataStore } from "@store/user_org";
import { useNotification } from "../../../../context/NotificationContext";

interface SignatureUploadWidgetProps {
  onSignatureUpdated?: (signatureUrl: string) => void;
}

export default function SignatureUploadWidget({ onSignatureUpdated }: SignatureUploadWidgetProps) {
  const userData = useDataStore((state) => state.userData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const { showNotification } = useNotification();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string>(userData?.infos?.signature_id || "");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !userData?.id) return;

    const file = e.target.files[0];

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showNotification({ message: "Veuillez sélectionner une image", type: "error" });
      return;
    }

    setUploading(true);

    try {
      // Upload signature
      const response = await usersApi.uploadSignature(userData.id, file);

      // Update preview
      setPreview(response.signature_url);

      // Notify parent
      if (onSignatureUpdated) {
        onSignatureUpdated(response.signature_url);
      }

      // Refetch user data to sync with backend
      await fetchUserData(userData.id);

      showNotification({ message: "Signature mise à jour avec succès", type: "success" });
    } catch (error) {
      console.error("Failed to upload signature:", error);
      showNotification({ message: "Échec du téléchargement de la signature", type: "error" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-sm font-semibold">Votre signature</div>
      <div className="text-xs text-gray-600">
        Cette signature sera utilisée pour tous vos documents
      </div>

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        id="signature-upload"
        disabled={uploading}
      />

      <label
        htmlFor="signature-upload"
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-gray-400 transition ${
          uploading ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {preview ? (
          <img src={preview} alt="Signature" className="max-h-24 mx-auto" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span className="text-xs">
              {uploading ? "Téléchargement..." : "Cliquez pour ajouter votre signature"}
            </span>
          </div>
        )}
      </label>

      {preview && !uploading && (
        <button
          onClick={() => document.getElementById("signature-upload")?.click()}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          Modifier la signature
        </button>
      )}
    </div>
  );
}
